import { apiUrl, authHeaders } from './auth';
import type { DonationDetail } from '~/components/Contributions/drawers/DonationDetailDrawer';

export type RefundPreview = {
    lookup_id: string;
    donation_amount: string;
    tip: { amount: string } | null;
    registration: null | {
        registration_id: number;
        registration_details: Array<{
            id: number;
            quantity: number;
            price_paid: string;
            line_cost: string;
            option_question_answer: string;
            addon_option_selection: string;
            registration_option: { id: number; name: string } | null;
            registration_addon: { id: number; name: string } | null;
        }>;
    };
    balance: {
        original_charge_amount: string;
        total_refunded_to_date: string;
        remaining_refundable: string;
    };
};

export type RefundRecord = {
    id: number;
    status: 'pending' | 'succeeded' | 'failed';
    stripe_refund_id: string;
    donation_refund_amount: string;
    tip_refund_amount: string;
    event_registration_items: any[];
    refunded_at: string | null;
    refunded_by_user_id: number;
    failure_reason: string;
    completion_status: 'pending' | 'complete';
    completed_at: string | null;
    completed_by_id: number | null;
};

export const fetchRefundPreview = async (
    lookupId: string
): Promise<RefundPreview> => {
    const resp = await fetch(
        `${apiUrl}/admin/donations/refund/preview?lookup_id=${encodeURIComponent(lookupId)}`,
        { headers: authHeaders() }
    );
    if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.detail || 'Failed to load refund preview');
    }
    return resp.json();
};

export const fetchRefundHistory = async (
    lookupId: string
): Promise<{ results: RefundRecord[]; balance: Record<string, string> }> => {
    const resp = await fetch(
        `${apiUrl}/admin/donations/refund?lookup_id=${encodeURIComponent(lookupId)}`,
        { headers: authHeaders() }
    );
    if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.detail || 'Failed to load refund history');
    }
    return resp.json();
};

export const markRefundComplete = async (
    refundId: number,
    status: 'complete' | 'pending' = 'complete'
): Promise<{
    id: number;
    completion_status: 'pending' | 'complete';
    completed_at: string | null;
    completed_by_id: number | null;
    // Who just made the change and when — reported separately because the
    // reopen branch clears completed_at/completed_by.
    completion_changed_by: string;
    completion_changed_at: string;
}> => {
    const resp = await fetch(
        `${apiUrl}/admin/donations/refund/${refundId}/complete`,
        {
            method: 'POST',
            headers: authHeaders(),
            body: JSON.stringify({ status }),
        }
    );
    if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.detail || 'Failed to update refund status');
    }
    return resp.json();
};

/**
 * The donation a refund came out of, in the Donation Detail drawer's shape.
 *
 * Keyed by refund rather than by donation id because a full refund deletes the
 * donation row — the endpoint rebuilds the record from the refund and the
 * Stripe charge in that case and flags it `donation_deleted`, so the refunds
 * list can open the drawer on any row.
 */
export const fetchRefundDonation = async (
    refundId: number
): Promise<DonationDetail> => {
    const resp = await fetch(
        `${apiUrl}/admin/donations/refund/${refundId}/donation`,
        { headers: authHeaders() }
    );
    if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.detail || 'Failed to load donation');
    }
    return resp.json();
};

export const submitRefund = async (payload: {
    lookup_id: string;
    donation_refund_amount: string;
    refund_tip: boolean;
    tip_refund_amount?: string;
    registration_items: { registration_detail_id: number; refund_quantity: number }[];
    cancel_note: string;
}) => {
    const resp = await fetch(`${apiUrl}/admin/donations/refund`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(payload),
    });
    if (!resp.ok) {
        const body = await resp.json().catch(() => ({}));
        throw new Error(body?.detail || 'Refund failed');
    }
    return resp.json();
};
